import React, { useEffect, useState } from 'react'
import { Button } from 'react-bootstrap'

/**
 * Banner de instalación PWA (Roger 30-may-2026).
 *
 * Aparece cada visita si:
 *  - El browser soporta beforeinstallprompt (Chrome Android, Edge)
 *  - El usuario NO tiene la PWA ya instalada (display-mode != standalone)
 *  - El usuario no clickó "Recordar más tarde" en las últimas 6h
 *
 * En iOS no se puede instalar programáticamente; mostramos texto explicativo
 * con icono compartir.
 */
const INSTALL_SUPPRESS_KEY = 'pwa-install-suppress-until'
const SUPPRESS_MS = 6 * 60 * 60 * 1000

const isStandalone = () =>
  (typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(display-mode: standalone)').matches) ||
  (typeof navigator !== 'undefined' && navigator.standalone === true)

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [show, setShow] = useState(false)
  const [installed, setInstalled] = useState(false)

  useEffect(() => {
    if (isStandalone()) {
      setInstalled(true)
      return
    }
    // Check suppress
    const suppressUntil = parseInt(localStorage.getItem(INSTALL_SUPPRESS_KEY) || '0', 10)
    if (suppressUntil > Date.now()) return

    const onBeforeInstall = (e) => {
      e.preventDefault()
      setDeferredPrompt(e)
      setShow(true)
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall)

    // iOS Safari: no dispara beforeinstallprompt, mostramos prompt manual
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
    if (isIOS && !isStandalone()) {
      setShow(true)
    }

    const onAppInstalled = () => {
      setInstalled(true)
      setShow(false)
    }
    window.addEventListener('appinstalled', onAppInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      window.removeEventListener('appinstalled', onAppInstalled)
    }
  }, [])

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt()
      const choice = await deferredPrompt.userChoice
      if (choice.outcome === 'accepted') {
        setInstalled(true)
        setShow(false)
      }
      setDeferredPrompt(null)
    }
  }

  const handleDismiss = () => {
    localStorage.setItem(INSTALL_SUPPRESS_KEY, String(Date.now() + SUPPRESS_MS))
    setShow(false)
  }

  if (installed || !show) return null

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 16,
        left: 16,
        right: 16,
        zIndex: 1080,
        background: '#fff',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        borderRadius: 8,
        padding: '12px 16px',
        border: '1px solid #ff6600',
      }}
    >
      <div className="d-flex align-items-center gap-2 mb-2">
        <span style={{ fontSize: 28 }}>📲</span>
        <div className="flex-grow-1">
          <div className="fw-bold" style={{ color: '#ff6600' }}>
            Instala Sirvent CRM
          </div>
          <small className="text-muted">
            {isIOS
              ? 'Toca compartir ⤴ y "Añadir a pantalla de inicio".'
              : 'Acceso directo desde la pantalla de inicio + carga más rápida.'}
          </small>
        </div>
      </div>
      <div className="d-flex gap-2 justify-content-end">
        <Button variant="outline-secondary" size="sm" onClick={handleDismiss}>
          Más tarde
        </Button>
        {!isIOS && deferredPrompt && (
          <Button variant="warning" size="sm" onClick={handleInstall}>
            Instalar
          </Button>
        )}
      </div>
    </div>
  )
}

export default PWAInstallPrompt
